import { useEffect, useRef, useCallback } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useGameStore } from '../store/gameStore'
import type { WSMessage } from '../types/api'
import { WS_BASE_URL } from '../utils/network'
import { getLastModalTrigger } from '../utils/modalEvents'

const STRICT_MODE_DISPOSE_DELAY = 100
const REMOTE_ACTION_ANIMATION_MS = 800

const ANIMATED_REMOTE_ACTIONS = new Set([
  'forage',
  'pollinate',
  'build_brood_chamber',
  'make_honey',
  'lay_egg',
  'clear_infection',
  'evolve',
])

let sharedSocket: Socket | null = null
let sharedDisposeTimer: ReturnType<typeof setTimeout> | null = null
let sharedSubscriberCount = 0
let sharedShouldReconnect = false
let sharedMessageHandler: (msg: WSMessage) => void = () => {}
let sharedBlockedMessageTimer: ReturnType<typeof setTimeout> | null = null
let sharedPendingBlockedMessage: WSMessage | null = null

function clearSharedDisposeTimer() {
  if (sharedDisposeTimer) {
    clearTimeout(sharedDisposeTimer)
    sharedDisposeTimer = null
  }
}

function clearSharedBlockedMessageTimer() {
  if (sharedBlockedMessageTimer) {
    clearTimeout(sharedBlockedMessageTimer)
    sharedBlockedMessageTimer = null
  }
}

function shouldBlockForRemoteAnimation(rawEvents: unknown[], memberColor: string | null): boolean {
  const currentPlayerColor = useGameStore.getState().room?.game?.current_player.color ?? null
  if (!memberColor || currentPlayerColor !== memberColor) {
    return false
  }

  return rawEvents.some((event) => {
    const payload = event as Record<string, unknown>
    if (payload.type !== 'game') {
      return false
    }

    const color = typeof payload.color === 'string' ? payload.color : null
    const action = typeof payload.action === 'string' ? payload.action : null
    if (!color || !action || color === memberColor) {
      return false
    }

    return ANIMATED_REMOTE_ACTIONS.has(action)
  })
}

export function useWebSocket() {
  const wsRef = useRef<Socket | null>(null)

  // Store setters don't change, use ref for handler to avoid stale closures
  const handleMessageRef = useRef<(msg: WSMessage) => void>(() => {})

  // Update the handler ref whenever deps change
  useEffect(() => {
    const {
      setRoom,
      setMember,
      setRoomId,
      setLobbyRooms,
      appendEvents,
      setError,
      setShowGameEnd,
      setShowQueenTrading,
      setShowSeasonOverview,
      setShowDonationResponse,
      setShowColonyCollapse,
      setDismissedSeasonOverviewSeq,
      setDismissedColonyCollapseOverviewSeq,
    } = useGameStore.getState()

    const processMessage = (message: WSMessage) => {
      const currentMemberId = useGameStore.getState().memberId
      switch (message.type) {
        case 'connected': {
          setMember(message.member)
          setRoomId(message.room_id)
          break
        }
        case 'room': {
          const newRoom = message.room
          const store = useGameStore.getState()
          store.setRoom(newRoom)
          store.setMember(newRoom.members.find((member) => member.id === currentMemberId) ?? null)
          // Auto-open relevant modals based on game phase
          if (newRoom.game) {
            const game = newRoom.game
            const myMember = store.member
            const myPlayer = myMember?.color
              ? game.players.find((p) => p.color === myMember.color)
              : null
            const isMyTurn =
              myPlayer &&
              game.current_player.color === myPlayer.color &&
              !myPlayer.has_passed

            store.setShowGameEnd(game.state !== 'in_progress')
            store.setShowQueenTrading(Boolean(game.phase === 'queen_trading' && isMyTurn))
            const seasonOverviewTrigger = getLastModalTrigger(newRoom, 'season-overview', myPlayer?.color ?? null)
            const dismissedSeq = store.dismissedSeasonOverviewSeq
            const shouldShowSeasonOverview =
              seasonOverviewTrigger?.action === 'open' &&
              seasonOverviewTrigger.seq !== undefined &&
              seasonOverviewTrigger.seq !== dismissedSeq
            if (seasonOverviewTrigger?.action === 'open' && seasonOverviewTrigger.seq !== dismissedSeq) {
              store.setDismissedSeasonOverviewSeq(null)
            }
            store.setShowSeasonOverview(Boolean(shouldShowSeasonOverview))

            let showDonationResponse = false
            if (game.phase === 'donation_response' && game.pending_donation) {
              const receiverColor = game.pending_donation.receiver
              if (myPlayer?.color === receiverColor) {
                showDonationResponse = true
              }
            }
            store.setShowDonationResponse(showDonationResponse)

            store.setShowColonyCollapse(game.phase === 'colony_collapse_offering')

            const ccOverviewTrigger = getLastModalTrigger(newRoom, 'colony-collapse-overview', myPlayer?.color ?? null)
            const dismissedCcSeq = store.dismissedColonyCollapseOverviewSeq
            const shouldShowCcOverview =
              ccOverviewTrigger?.action === 'open' &&
              ccOverviewTrigger.seq !== undefined &&
              ccOverviewTrigger.seq !== dismissedCcSeq
            if (ccOverviewTrigger?.action === 'open' && ccOverviewTrigger.seq !== dismissedCcSeq) {
              store.setDismissedColonyCollapseOverviewSeq(null)
            }
            store.setShowColonyCollapseOverview(Boolean(shouldShowCcOverview))
          } else {
            store.setShowGameEnd(false)
            store.setShowQueenTrading(false)
            store.setShowSeasonOverview(false)
            store.setShowDonationResponse(false)
            store.setShowColonyCollapse(false)
            store.setShowColonyCollapseOverview(false)
            store.setDismissedSeasonOverviewSeq(null)
            store.setDismissedColonyCollapseOverviewSeq(null)
          }
          break
        }
        case 'events': {
          const store = useGameStore.getState()
          const memberColor = store.member?.color ?? null
          console.debug('[ws-events] received events message', {
            count: message.events?.length ?? 0,
            events: (message.events || []).map((event) => {
              const payload = event as Record<string, unknown>
              const target = payload.target as Record<string, unknown> | null | undefined
              return {
                seq: payload.seq ?? null,
                type: payload.type ?? null,
                color: payload.color ?? null,
                action: payload.action ?? null,
                targetId: target?.id ?? null,
              }
            }),
            memberColor,
            roomTip: store.room?.tip ?? null,
            animationBlockUntil: store.animationBlockUntil,
          })
          if (shouldBlockForRemoteAnimation(message.events || [], memberColor)) {
            store.setAnimationBlockUntil(Date.now() + REMOTE_ACTION_ANIMATION_MS)
          }

          const events = (message.events || []).map((e: unknown) => ({
            seq: (e as Record<string, unknown>).seq as number,
            type: (e as Record<string, unknown>).type as string,
            data: e as Record<string, unknown>,
            timestamp: Date.now(),
          }))
          store.appendEvents(events)
          break
        }
        case 'lobby_rooms': {
          setLobbyRooms(message.rooms)
          break
        }
        case 'room_assignment': {
          useGameStore.getState().setRoomId(message.room_id)
          // Request sync
          if (wsRef.current?.connected) {
            wsRef.current.emit('message', { type: 'sync' })
          }
          break
        }
        case 'room_deleted': {
          useGameStore.getState().setRoomId(null)
          useGameStore.getState().setRoom(null)
          break
        }
        case 'pong':
          break
        case 'error': {
          const store = useGameStore.getState()
          store.setError(message.message)
          setTimeout(() => store.setError(null), 5000)
          break
        }
      }
    }

    handleMessageRef.current = (message: WSMessage) => {
      const blockUntil = useGameStore.getState().animationBlockUntil
      const isDelayable = message.type === 'room' || message.type === 'room_deleted' || message.type === 'events'
      if (isDelayable && blockUntil > Date.now()) {
        sharedPendingBlockedMessage = message
        clearSharedBlockedMessageTimer()
        sharedBlockedMessageTimer = setTimeout(() => {
          const pending = sharedPendingBlockedMessage
          sharedPendingBlockedMessage = null
          sharedBlockedMessageTimer = null
          if (pending) {
            processMessage(pending)
          }
        }, blockUntil - Date.now())
        return
      }

      processMessage(message)
    }
    sharedMessageHandler = handleMessageRef.current
  })

  const connect = useCallback(() => {
    if (!sharedShouldReconnect || sharedSubscriberCount <= 0) return
    if (sharedSocket && (sharedSocket.connected || sharedSocket.active)) {
      wsRef.current = sharedSocket
      return
    }

    const buildAuth = () => {
      const { memberId, sessionId, alias } = useGameStore.getState()
      const savedRoomId = localStorage.getItem('cc_room_id')
      return {
        session_id: sessionId,
        member_id: memberId,
        alias,
        ...(savedRoomId ? { room_id: savedRoomId } : {}),
      }
    }

    try {
      const ws = io(WS_BASE_URL, {
        autoConnect: true,
        auth: buildAuth(),
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        randomizationFactor: 0.2,
        timeout: 20000,
      })
      sharedSocket = ws
      wsRef.current = ws

      ws.io.on('reconnect_attempt', () => {
        ws.auth = buildAuth()
      })

      ws.on('connect', () => {
        ws.auth = buildAuth()
      })

      ws.on('message', (message: WSMessage) => {
        console.debug('[ws-raw] onmessage', {
          type: message.type,
          roomId: 'room_id' in message ? message.room_id : null,
          tip: 'tip' in message ? message.tip : null,
          eventCount: message.type === 'events' ? message.events?.length ?? 0 : null,
          events: message.type === 'events'
            ? (message.events || []).map((item) => {
                const payload = item as Record<string, unknown>
                const target = payload.target as Record<string, unknown> | null | undefined
                return {
                  seq: payload.seq ?? null,
                  type: payload.type ?? null,
                  color: payload.color ?? null,
                  action: payload.action ?? null,
                  targetId: target?.id ?? null,
                }
              })
            : null,
        })
        sharedMessageHandler(message)
      })

      ws.on('disconnect', (reason: string) => {
        if (sharedSocket !== ws) {
          return
        }
        if (reason === 'io server disconnect' && sharedShouldReconnect && sharedSubscriberCount > 0) {
          ws.auth = buildAuth()
          ws.connect()
        }
      })
    } catch {
      sharedSocket = null
    }
  }, [])

  const sendSync = useCallback(() => {
    if (sharedSocket?.connected) {
      sharedSocket.emit('message', { type: 'sync' })
    }
  }, [])

  useEffect(() => {
    sharedSubscriberCount += 1
    sharedShouldReconnect = true
    clearSharedDisposeTimer()
    connect()

    return () => {
      sharedSubscriberCount = Math.max(0, sharedSubscriberCount - 1)
      if (sharedSubscriberCount > 0) {
        return
      }

      clearSharedDisposeTimer()
      sharedDisposeTimer = setTimeout(() => {
        if (sharedSubscriberCount > 0) {
          return
        }
        sharedShouldReconnect = false
        clearSharedBlockedMessageTimer()
        sharedPendingBlockedMessage = null
        if (sharedSocket) {
          sharedSocket.disconnect()
        }
        sharedSocket = null
      }, STRICT_MODE_DISPOSE_DELAY)
    }
  }, [connect])

  return { sendSync }
}
